// 前端 API 客户端 - 用于调用后端 API
import { ensureSpatialMapDataUrl, normalizeFixtureForRender } from '../utils/fixtureNormalize';

// 动态导入 supabase，避免循环依赖
let supabase: any = null;

async function getSupabaseClient() {
  if (!supabase) {
    const { supabase: supabaseClient } = await import('../src/lib/supabaseClient');
    supabase = supabaseClient;
  }
  return supabase;
}

// ============================================================================
// Step 2：API base URL 生产保护
// ----------------------------------------------------------------------------
// 生产环境一律相对路径 ''，通过 Nginx 反代到后端；
// VITE_API_BASE_URL / VITE_API_URL 只在本地开发生效；
// 生产域名下若误配 localhost / 公网 host，console.error 并强制回落到 ''。
// ============================================================================

function isLocalDevHostname(hostname: string): boolean {
  if (!hostname) return false;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0' || hostname === '::1') {
    return true;
  }
  if (/^192\.168\./.test(hostname)) return true;
  if (/^10\./.test(hostname)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(hostname)) return true;
  return false;
}

function isLocalApiBase(value: string): boolean {
  if (!value) return false;
  // 直接以主机部分判断；既支持 'http://localhost:3003' 也支持 '//127.0.0.1:3003'
  try {
    const u = new URL(value, 'http://placeholder.invalid');
    if (u.host === 'placeholder.invalid') {
      // 相对路径（如 '/api'）—— 不是绝对地址，直接放行
      return false;
    }
    return isLocalDevHostname(u.hostname);
  } catch {
    return /(localhost|127\.0\.0\.1)/i.test(value);
  }
}

function resolveApiBase(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env || {};
  const configuredRaw = (env.VITE_API_BASE_URL ?? env.VITE_API_URL ?? '').trim();
  const configured = configuredRaw.replace(/\/+$/, '');

  const hasWindow = typeof window !== 'undefined';
  const hostname = hasWindow ? window.location.hostname || '' : '';
  const isDevBuild = env.DEV === true || (env.MODE && env.MODE !== 'production');
  const isLocalHost = isLocalDevHostname(hostname);
  const isLocalEnv = !!isDevBuild || isLocalHost;

  if (isLocalEnv) {
    // 本地开发：允许使用 VITE_API_BASE_URL / VITE_API_URL；未配置则相对路径
    const base = configured || '';
    console.info('[apiClient] API base =', base || '(relative)', {
      mode: env.MODE,
      dev: isDevBuild,
      hostname,
    });
    return base;
  }

  // 生产环境：一律相对路径，禁止任何绝对 host
  if (!configured) {
    console.info('[apiClient] API base = (relative)', { hostname });
    return '';
  }
  if (isLocalApiBase(configured)) {
    console.error(
      '[apiClient] Refused localhost API base in production, fallback to relative /api',
      { configured, hostname },
    );
    return '';
  }
  console.warn(
    '[apiClient] Ignoring non-relative API base in production (use Nginx /api reverse-proxy)',
    { configured, hostname },
  );
  return '';
}

const API_BASE_URL = resolveApiBase();

// ============================================================================
// ApiError / ApiErrorCode（Step 1：统一错误分类 + token gate）
// ----------------------------------------------------------------------------
// 兼容旧业务层：ApiError extends Error，message/status/isInsufficientBalance 等
// 旧字段保留；新代码推荐 `e instanceof ApiError && e.code === '...'`。
// ============================================================================

export type ApiErrorCode =
  | 'auth_not_ready'
  | 'unauthorized'
  | 'forbidden'
  | 'insufficient_credits'
  | 'not_found'
  | 'rate_limited'
  | 'backend_5xx'
  | 'gateway'
  | 'maintenance'
  | 'network_unreachable'
  | 'timeout'
  | 'bad_response'
  | 'unknown';

export interface ApiErrorInit {
  code: ApiErrorCode;
  message: string;
  url?: string;
  method?: string;
  status?: number;
  requestId?: string;
  /** 后端 body.code，如 'MISSING_TOKEN' / 'INSUFFICIENT_CREDITS' */
  serverCode?: string;
  originalMessage?: string;
  originalError?: unknown;
  details?: unknown;
  /** 兼容字段：isInsufficientBalance / currentBalance / remainingCredits / hint 等 */
  extras?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status?: number;
  readonly url?: string;
  readonly method?: string;
  readonly requestId?: string;
  readonly serverCode?: string;
  readonly originalMessage: string;
  readonly originalError?: unknown;
  readonly details?: unknown;
  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.url = init.url;
    this.method = init.method;
    this.requestId = init.requestId;
    this.serverCode = init.serverCode;
    this.originalMessage = init.originalMessage ?? init.message;
    this.originalError = init.originalError;
    this.details = init.details;
    if (init.extras) {
      for (const [k, v] of Object.entries(init.extras)) {
        if (v === undefined) continue;
        if (k in this) continue;
        (this as unknown as Record<string, unknown>)[k] = v;
      }
    }
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

type AuthTokenContext = { url: string; method: string };

/**
 * 拿不到 token 时返回 null，不抛错；用于 skipAuth / requireAuth=false 路径。
 */
async function getAuthTokenOptional(): Promise<string | null> {
  try {
    const client = await getSupabaseClient();
    const { data: { session }, error } = await client.auth.getSession();
    if (error) {
      console.warn('[API] 获取 session 失败:', error.message);
      return null;
    }
    if (!session || !session.access_token) return null;
    return session.access_token;
  } catch (error) {
    console.warn('[API] 获取认证 token 异常:', error);
    return null;
  }
}

/**
 * 必须 token：拿不到 session.access_token 时直接抛 ApiError(code='auth_not_ready')。
 * 调用方应跳过 fetch，避免向后端发起注定 401 的请求。
 */
async function requireAuthToken(ctx: AuthTokenContext): Promise<string> {
  let session: { access_token?: string } | null = null;
  let originalError: unknown;
  try {
    const client = await getSupabaseClient();
    const { data, error } = await client.auth.getSession();
    if (error) {
      originalError = error;
    } else {
      session = (data?.session as { access_token?: string } | null) ?? null;
    }
  } catch (e) {
    originalError = e;
  }
  const accessToken = session?.access_token;
  if (!accessToken) {
    const reason = !session ? 'no_session' : 'no_access_token';
    console.info('[API] auth_not_ready, skip fetch', {
      url: ctx.url,
      method: ctx.method,
      reason,
    });
    throw new ApiError({
      code: 'auth_not_ready',
      message: '登录状态尚未就绪，请稍后重试',
      url: ctx.url,
      method: ctx.method,
      serverCode: reason,
      originalMessage: reason,
      originalError,
    });
  }
  return accessToken;
}

/**
 * @deprecated 仅为向后兼容；新代码请使用 requireAuthToken / getAuthTokenOptional。
 */
async function getAuthToken(): Promise<string | null> {
  return getAuthTokenOptional();
}

// 保留旧的 token 管理函数（向后兼容）
export function setAuthToken(token: string) {
  // 不再使用 localStorage，直接使用 Supabase session
  console.warn('setAuthToken 已废弃，现在使用 Supabase Auth');
}

export function clearAuthToken() {
  // 不再使用 localStorage
  console.warn('clearAuthToken 已废弃，现在使用 Supabase Auth');
}

/** Nginx 返回 HTML 错误页时不要整段塞进 toast */
function formatNonJsonErrorBody(status: number, rawText: string): string {
  const trimmed = rawText.trim();
  const looksHtml =
    /^<\s*!doctype/i.test(trimmed) ||
    /^<\s*html/i.test(trimmed) ||
    /<head[\s>]|<body[\s>]/i.test(trimmed) ||
    /504\s+Gateway|503\s+Service Unavailable|502\s+Bad Gateway|Gateway\s+Time-?out/i.test(trimmed);

  if (status === 504 || /Gateway\s+Time-?out/i.test(trimmed)) {
    return '生成超时或网关超时（504）：上游处理时间过长，请稍后重试。若经常出现，请在 Nginx 上调大 proxy_read_timeout / proxy_send_timeout（可参考 deploy/nginx-proxy-long-timeout.example.conf）。';
  }
  if (status === 503) {
    return '服务暂时不可用（503）：服务器繁忙或维护中，请稍后重试。';
  }
  if (status === 502) {
    return '网关错误（502）：无法及时连接上游服务，请稍后重试。';
  }
  if (looksHtml) {
    return `请求失败（${status}）：网关返回了 HTML 错误页（非接口 JSON），请检查服务器日志或稍后重试。`;
  }
  const max = 280;
  if (!trimmed) return `请求失败 (${status})`;
  return trimmed.length <= max ? `请求失败 (${status}): ${trimmed}` : `请求失败 (${status}): ${trimmed.slice(0, max)}…`;
}

// 通用请求函数
async function request(
  endpoint: string,
  options: RequestInit & { timeout?: number; skipAuth?: boolean; requireAuth?: boolean } = {},
) {
  const url = `${API_BASE_URL}${endpoint}`;
  const method = (options.method || 'GET').toUpperCase();
  // 默认所有请求都需要 auth；显式 requireAuth: false 或旧的 skipAuth: true 时跳过 token gate。
  const requireAuth = options.requireAuth ?? !options.skipAuth;

  // ── Token gate：requireAuth=true 时无 token 直接抛 auth_not_ready，不发 fetch ──
  let authToken: string | null = null;
  if (requireAuth) {
    authToken = await requireAuthToken({ url, method });
  } else {
    authToken = await getAuthTokenOptional();
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(authToken && { Authorization: `Bearer ${authToken}` }),
    ...options.headers,
  };

  // 🔥 图片分析/渲染：默认 180s；色调映射 / 高清放大 RunningHub 链路 300s
  const isToneMapRequest = endpoint.includes('/api/gemini/runninghub/tone-map');
  const isRelightRequest = endpoint.includes('/api/gemini/runninghub/relight');
  const isTextReconstructRequest = endpoint.includes('/api/gemini/runninghub/text-reconstruct');
  const isPhysicalReconstructRequest = endpoint.includes('/api/gemini/runninghub/physical-reconstruct');
  const isUpscaleRunRequest = endpoint.includes('/api/upscale/upscale');
  const isImageAnalyzeOrRenderRequest =
    endpoint.includes('/api/gemini/analyze') ||
    endpoint.includes('/api/gemini/render') ||
    endpoint.includes('/api/render/generate') ||
    isToneMapRequest ||
    endpoint.includes('/api/gemini/runninghub/depth-normal') ||
    endpoint.includes('/api/gemini/upload-for-photopea') ||
    isUpscaleRunRequest;
  const defaultTimeout =
    isToneMapRequest || isRelightRequest || isTextReconstructRequest || isPhysicalReconstructRequest || isUpscaleRunRequest
      ? 300000
      : isImageAnalyzeOrRenderRequest
        ? 180000
        : 60000;
  const timeoutMs = options.timeout ?? defaultTimeout;
  // 分离 timeout / skipAuth / requireAuth，不传递给 fetch
  const { timeout: _timeoutIgnored, skipAuth: _skipAuthIgnored, requireAuth: _requireAuthIgnored, ...fetchOptions } = options;
  void _timeoutIgnored; void _skipAuthIgnored; void _requireAuthIgnored;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
    console.error(`[API请求] ⚠️  请求超时（${timeoutMs / 1000}秒）: ${endpoint}`);
  }, timeoutMs);

  let response: Response;
  try {
    response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal, // 🔥 添加超时信号
    });
    clearTimeout(timeoutId);
  } catch (fetchError: any) {
    clearTimeout(timeoutId);
    // 1) 客户端超时 / 中断
    if (fetchError?.name === 'AbortError' || controller.signal.aborted) {
      const msg = `请求超时（${timeoutMs / 1000}秒），请检查网络连接或稍后重试`;
      console.error(`[API请求] ❌ 请求超时（${timeoutMs / 1000}秒）: ${endpoint}`);
      throw new ApiError({
        code: 'timeout',
        message: msg,
        url,
        method,
        originalMessage: fetchError?.message || msg,
        originalError: fetchError,
      });
    }
    // 2) 网络不可达
    const original = fetchError?.message || String(fetchError || '');
    const looksNetwork =
      typeof original === 'string' &&
      (original.includes('Failed to fetch') ||
        original.includes('Load failed') ||
        original.includes('NetworkError') ||
        original.includes('ECONNREFUSED'));
    if (looksNetwork) {
      const msg = '无法连接后端服务，请确认已启动后端（nemo-light-backend）。Unable to connect to backend.';
      console.warn('[API请求] 🌐 网络不可达', { url, method, message: original });
      throw new ApiError({
        code: 'network_unreachable',
        message: msg,
        url,
        method,
        originalMessage: original,
        originalError: fetchError,
      });
    }
    // 3) 未分类
    console.error('[API请求] ❌ fetch 异常（未分类）', { url, method, message: original });
    throw new ApiError({
      code: 'unknown',
      message: original || '请求失败',
      url,
      method,
      originalMessage: original,
      originalError: fetchError,
    });
  }

  const status = response.status;
  const requestId =
    response.headers.get('x-request-id') ||
    response.headers.get('x-amzn-requestid') ||
    response.headers.get('cf-ray') ||
    undefined;

  // ── 401: 未授权（保留旧文案：未授权，请重新登录） ──
  if (status === 401) {
    let body: any = {};
    try {
      body = await response.json();
    } catch {
      body = {};
    }
    const serverCode = typeof body?.code === 'string' ? body.code : undefined;
    if (options.skipAuth) {
      // 兼容旧逻辑：跳过认证的请求收到 401，保留后端 message 作为顶层文案
      const msg = body?.message || body?.error || '请求失败，请稍后重试';
      console.error('⚠️  请求未授权，但这是不需要认证的请求', { url, method, requestId, serverCode });
      throw new ApiError({
        code: 'unauthorized',
        message: msg,
        url,
        method,
        status,
        requestId,
        serverCode,
        originalMessage: msg,
        details: body,
      });
    }
    console.warn('⚠️  请求未授权，Token 可能已过期', { url, method, requestId, serverCode });
    throw new ApiError({
      code: 'unauthorized',
      message: '未授权，请重新登录',
      url,
      method,
      status,
      requestId,
      serverCode,
      originalMessage: body?.message || body?.error || '未授权，请重新登录',
      details: body,
    });
  }

  if (!response.ok) {
    // 色板影调摘要为可选能力：线上未部署该路由时返回 404，不刷屏、不抛错，由前端启发式兜底
    if (status === 404 && endpoint.includes('/api/gemini/palette-summary')) {
      return { summary: null as string | null };
    }
    // 先读正文再解析：避免 Content-Type 与正文不一致、或空 body 时看不到任何信息
    let errorData: any = {};
    const rawText = await response.text().catch(() => '');
    const trimmed = rawText.trim();
    let parsedJson = false;
    try {
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          errorData = parsed;
          parsedJson = true;
        }
      }
    } catch {
      /* 非 JSON */
    }
    const emptyBackendHint =
      '响应体为空或无法解析。请确认 nemo-light-backend 已启动（端口以 .env 的 PORT 为准，并与 Vite 代理一致），并访问 /health；同时查看后端终端全局错误日志。';
    if (!errorData || typeof errorData !== 'object') {
      errorData = {};
    }
    const jsonErr = String(errorData.error || errorData.message || '').trim();
    if (!jsonErr) {
      errorData = trimmed
        ? { error: formatNonJsonErrorBody(status, trimmed) }
        : { error: `请求失败 (${status})：${emptyBackendHint}` };
    } else {
      if (!errorData.error) errorData.error = errorData.message || jsonErr;
      if (!errorData.message) errorData.message = errorData.error || jsonErr;
    }

    // 🔥🔥🔥 强制打印完整错误信息到浏览器控制台（保留旧诊断日志）
    console.error('═══════════════════════════════════════════════════════════');
    console.error('❌ [前端API] 请求失败');
    console.error('═══════════════════════════════════════════════════════════');
    console.error('   请求路径:', endpoint);
    console.error('   完整 URL:', url);
    console.error('   状态码:', status);
    console.error('   错误信息:', errorData.error || errorData.message || '未知错误');
    console.error('   错误代码:', errorData.code || '无');
    console.error('   错误提示 (hint):', errorData.hint || '无');
    console.error('   错误详情 (details):', errorData.details || '无');
    console.error('   完整错误对象:', JSON.stringify(errorData, null, 2));

    const errorMessage = errorData.error || errorData.message || '';
    if (
      errorData.isRLSError ||
      errorMessage.includes('RLS') ||
      errorMessage.includes('row-level security') ||
      errorMessage.includes('violates row-level security')
    ) {
      console.error('\n🔒 [RLS 错误检测]');
      console.error('   这是一个 Row Level Security (RLS) 错误！');
      console.error('   可能的原因:');
      console.error('   1. 后端使用了 ANON_KEY 而不是 SERVICE_ROLE_KEY');
      console.error('   2. users 表的 RLS 策略阻止了更新操作');
      console.error('   3. 环境变量配置错误');
      console.error('\n   解决方案:');
      console.error('   1. 检查后端日志，确认使用的密钥类型');
      console.error('   2. 确认后端使用的是 SERVICE_ROLE_KEY');
      console.error('   3. 检查 nemo-light-backend/.env 文件');
    }
    if (errorData.isPermissionError || errorMessage.includes('permission denied')) {
      console.error('\n🔒 [权限错误]');
      console.error('   权限被拒绝！');
      console.error('   很可能使用了错误的密钥类型（ANON_KEY vs SERVICE_ROLE_KEY）');
    }
    console.error('═══════════════════════════════════════════════════════════');

    let msg = errorData.error || errorData.message || `请求失败: ${status}`;
    if (
      status === 400 &&
      String(errorData.error || '').trim() === '输入验证失败' &&
      Array.isArray(errorData.details) &&
      errorData.details.length > 0
    ) {
      const d0 = errorData.details[0];
      const path = d0?.path != null ? String(d0.path) : '';
      const detailMsg = d0?.message != null ? String(d0.message) : '';
      if (path || detailMsg) {
        msg = `${msg} (${path}: ${detailMsg})`;
      }
    }
    const serverCode = typeof errorData.code === 'string' ? errorData.code : undefined;
    const baseInit = {
      message: msg,
      url,
      method,
      status,
      requestId,
      serverCode,
      originalMessage: msg,
      details: errorData,
    } as const;

    // 402 / 403：余额不足 / 扣费失败 — 保留 isInsufficientBalance / currentBalance / remainingCredits / hint 等兼容字段
    if (status === 402 || status === 403) {
      const isInsufficientBalance =
        serverCode === 'INSUFFICIENT_CREDITS' ||
        errorData.error === '积分不足' ||
        (typeof errorData.message === 'string' && errorData.message.includes('积分不足'));
      const code: ApiErrorCode =
        status === 402 || isInsufficientBalance ? 'insufficient_credits' : 'forbidden';
      throw new ApiError({
        ...baseInit,
        code,
        extras: {
          currentBalance: errorData.currentBalance,
          remainingCredits: errorData.remainingCredits,
          isInsufficientBalance,
          hint: errorData.hint,
          isRLSError: errorData.isRLSError,
          isPermissionError: errorData.isPermissionError,
        },
      });
    }

    if (status === 404) {
      throw new ApiError({
        ...baseInit,
        code: 'not_found',
        extras: { hint: errorData.hint },
      });
    }

    if (status === 429) {
      throw new ApiError({
        ...baseInit,
        code: 'rate_limited',
        extras: { hint: errorData.hint },
      });
    }

    if (status >= 500 && status < 600) {
      const isMaintenance =
        status === 503 &&
        (errorData?.status === 'maintenance' || errorData?.maintenance === true);
      const looksHtml = !parsedJson && /<\s*html|<\s*!doctype/i.test(trimmed);
      const isGatewayLike = status === 502 || status === 503 || status === 504 || looksHtml;
      const code: ApiErrorCode = isMaintenance ? 'maintenance' : isGatewayLike ? 'gateway' : 'backend_5xx';
      throw new ApiError({
        ...baseInit,
        code,
        extras: {
          hint: errorData.hint,
          isRLSError: errorData.isRLSError,
          isPermissionError: errorData.isPermissionError,
        },
      });
    }

    // 其余 4xx / 不可识别状态 → bad_response（保留旧 message，例如 400/422）
    throw new ApiError({
      ...baseInit,
      code: 'bad_response',
      extras: {
        hint: errorData.hint,
        isRLSError: errorData.isRLSError,
        isPermissionError: errorData.isPermissionError,
      },
    });
  }

  // ── 成功响应：先短路 204 / Content-Length=0 / 空 body，再尝试 JSON.parse ──
  // body 只能消费一次，必须用 response.text() 自行解析，不能再调 response.json()
  if (status === 204 || response.headers.get('content-length') === '0') {
    console.log('[API请求] ✅ 请求成功（空响应）:', { endpoint, status, reason: status === 204 ? '204' : 'content-length-0' });
    return null;
  }

  let rawText = '';
  try {
    rawText = await response.text();
  } catch (e: any) {
    const original = e?.message || String(e);
    console.error('[API请求] ⚠️ 响应体读取失败', { url, method, status, message: original });
    throw new ApiError({
      code: 'bad_response',
      message: `响应解析失败（${status}）：${original}`,
      url,
      method,
      status,
      requestId,
      originalMessage: original,
      originalError: e,
    });
  }

  if (!rawText || rawText.trim() === '') {
    console.log('[API请求] ✅ 请求成功（空 body）:', { endpoint, status });
    return null;
  }

  let responseData: any;
  try {
    responseData = JSON.parse(rawText);
  } catch (e: any) {
    const original = e?.message || String(e);
    console.error('[API请求] ⚠️ 响应体解析失败', {
      url,
      method,
      status,
      message: original,
      bodyPreview: rawText.slice(0, 200),
    });
    throw new ApiError({
      code: 'bad_response',
      message: `响应解析失败（${status}）：${original}`,
      url,
      method,
      status,
      requestId,
      originalMessage: original,
      originalError: e,
    });
  }

  console.log('[API请求] ✅ 请求成功:', {
    endpoint,
    status,
    contentType: response.headers.get('content-type'),
    dataType: typeof responseData,
    dataKeys: responseData ? Object.keys(responseData) : 'null',
    hasFixtures: 'fixtures' in (responseData || {}),
    fixturesType: Array.isArray(responseData?.fixtures) ? 'array' : typeof responseData?.fixtures,
    fixturesLength: Array.isArray(responseData?.fixtures) ? responseData.fixtures.length : 'not array',
    dataPreview: JSON.stringify(responseData, null, 2).substring(0, 500),
  });
  return responseData;
}

/** 与后端 validation 中 data URL 白名单一致（前端据此决定是否需要经 canvas 转 JPEG） */
const RUNNINGHUB_ACCEPTED_DATA_URL_RE =
  /^data:image\/(png|jpe?g|pjpeg|webp|gif|avif|bmp|x-ms-bmp|tif|tiff|heic|heif);base64,/i;

function rasterDataUrlToJpegDataUrl(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const nw = img.naturalWidth || 1024;
        const nh = img.naturalHeight || 1024;
        const w = Math.min(nw, 8192);
        const h = Math.min(nh, 8192);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('无法创建画布'));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      } catch (e: unknown) {
        reject(e instanceof Error ? e : new Error('导出 JPEG 失败'));
      }
    };
    img.onerror = () => reject(new Error('图片无法解码，请换 PNG/JPEG 上传'));
    img.src = dataUrl;
  });
}

/**
 * COS/外链 HTTPS → 经后端 fetch-image 转 data URL；非常见 data:image/* → canvas 转 JPEG。
 * 解决「画布上是 COS 直链或 AVIF」时 POST runninghub/* 校验 400。
 */
async function normalizeImageForRunningHubBody(image: string): Promise<string> {
  const s = String(image || '').trim();
  if (!s) throw new Error('缺少图片');
  if (/^https?:\/\//i.test(s)) {
    const res = (await request('/api/gemini/fetch-image', {
      method: 'POST',
      body: JSON.stringify({ url: s }),
    })) as { dataUrl?: string };
    if (!res?.dataUrl || typeof res.dataUrl !== 'string') {
      throw new Error('无法将图片地址转为可用的数据格式');
    }
    return normalizeImageForRunningHubBody(res.dataUrl);
  }
  if (RUNNINGHUB_ACCEPTED_DATA_URL_RE.test(s)) return s;
  if (/^data:image\//i.test(s) && /;base64,/i.test(s)) {
    return rasterDataUrlToJpegDataUrl(s);
  }
  throw new Error('图片须为 data URL 或 HTTPS 地址');
}

// API 方法
export const api = {
  // Gemini API
  async createLightingTask() {
    return request('/api/tasks', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  /** 由后端请求 RUNNINGHUB_BASE_URL，检测 TLS/HTTP 是否可达（不提交任务、不扣费） */
  async checkRunningHubLink(): Promise<{ ok: boolean; host?: string }> {
    try {
      const data = (await request('/api/gemini/runninghub/link-check', {
        method: 'GET',
        timeout: 15000,
        skipAuth: true,
      })) as { ok?: boolean; host?: string };
      return { ok: !!data?.ok, host: data?.host };
    } catch {
      return { ok: false };
    }
  },

  async analyzeImage(image: string, taskId?: string | null) {
    const body: { image: string; taskId?: string } = { image };
    if (taskId) body.taskId = taskId;
    return request('/api/gemini/analyze', {
      method: 'POST',
      timeout: 180000,
      body: JSON.stringify(body),
    });
  },

  async analyzeDebug(image: string) {
    return request('/api/gemini/analyze-debug', {
      method: 'POST',
      timeout: 180000,
      body: JSON.stringify({ image }),
    });
  },

  /**
   * 色板 HEX → 影调摘要（后端文本模型）。
   * 默认不发起请求，避免未部署 /api/gemini/palette-summary 时控制台出现 404 “Failed to load resource”。
   * 部署该路由后，在 .env 设置 VITE_ENABLE_PALETTE_SUMMARY_API=true 并重新构建前端。
   */
  async paletteSummary(hexColors: string[]) {
    if (import.meta.env.VITE_ENABLE_PALETTE_SUMMARY_API !== 'true') {
      return { summary: null as string | null };
    }
    return request('/api/gemini/palette-summary', {
      method: 'POST',
      body: JSON.stringify({ hexColors }),
    });
  },
  
  async renderImage(
    image: string, 
    fixtures: any[], 
    dimensions: any, 
    userEmail?: string,
    maskImage?: string | null,
    spotlightPrompts?: { [key: string]: string },
    toneMappingPbr?: { paletteHex: string[]; dnaSummary?: string; shadowImageDataUrl?: string } | null,
    customInstruction?: string,
    commandMode?: boolean,
    referenceImage?: string,
    enginePolicy?: 'default' | 'laozhang_only',
    depthImage?: string,
    normalImage?: string,
  ) {
    const normalizedFixtures = (fixtures || []).map((f) =>
      normalizeFixtureForRender(f && typeof f === 'object' ? f : {}),
    );
    const fetchSpatial = (url: string) =>
      request('/api/gemini/fetch-image', { method: 'POST', body: JSON.stringify({ url }) });
    const depthOut = await ensureSpatialMapDataUrl(depthImage, fetchSpatial);
    const normalOut = await ensureSpatialMapDataUrl(normalImage, fetchSpatial);

    // 与 POST /api/render/generate 同源（handleGeminiRender、先扣费）；旧 dist/未重启后端仅有 /api/gemini/render
    return request('/api/gemini/render', {
      method: 'POST',
      timeout: 180000,
      body: JSON.stringify({ 
        image, 
        fixtures: normalizedFixtures, 
        dimensions, 
        userEmail,
        maskImage, // 🔥 传递蒙版
        spotlightPrompts, // 🔥 传递参数化提示词
        toneMappingPbr: toneMappingPbr ?? undefined,
        customInstruction: customInstruction?.trim() || undefined,
        commandMode: !!commandMode,
        referenceImage: referenceImage?.trim() || undefined,
        enginePolicy: enginePolicy === 'laozhang_only' ? 'laozhang_only' : undefined,
        ...(depthOut && normalOut ? { depthImage: depthOut, normalImage: normalOut } : {}),
      }),
    });
  },

  /**
   * 色调映射：POST /api/gemini/runninghub/tone-map。
   * 路径中的 gemini 仅为路由器挂载前缀；服务端对该路由只调用 RunningHub，不调用 Google Gemini。
   */
  async submitToneMapRunningHub(
    image: string,
    dimensions: { width: number; height: number },
    userEmail: string | undefined,
    toneMappingPbr: { paletteHex: string[]; dnaSummary?: string; shadowImageDataUrl?: string },
    jobMeta?: { clientItemId: string; historyPayloadDraft: Record<string, unknown> },
  ) {
    const normalized = await normalizeImageForRunningHubBody(image);
    return request('/api/gemini/runninghub/tone-map', {
      method: 'POST',
      timeout: 300000,
      body: JSON.stringify({
        image: normalized,
        dimensions,
        userEmail,
        toneMappingPbr,
        ...jobMeta,
      }),
    });
  },

  /**
   * 色调映射兼容入口：POST /api/gemini/render + toneMappingPbr（fixtures 为空）。
   * 同上，仅因历史路由命名含 gemini；带 toneMappingPbr 时服务端只走 RunningHub。
   */
  async submitToneMapRenderCompat(
    image: string,
    dimensions: { width: number; height: number },
    userEmail: string | undefined,
    toneMappingPbr: { paletteHex: string[]; dnaSummary?: string; shadowImageDataUrl?: string },
  ) {
    return this.renderImage(image, [], dimensions, userEmail, null, undefined, toneMappingPbr);
  },

  /**
   * 深度图 + 法线图：POST /api/gemini/runninghub/depth-normal（仅 RunningHub；不扣积分）
   */
  async submitDepthNormalRunningHub(
    image: string,
    dimensions: { width: number; height: number },
    userEmail: string | undefined,
  ) {
    const normalized = await normalizeImageForRunningHubBody(image);
    return request('/api/gemini/runninghub/depth-normal', {
      method: 'POST',
      body: JSON.stringify({ image: normalized, dimensions, userEmail }),
    });
  },

  async submitRelightRunningHub(
    image: string,
    mode: 'sunlight_shadow' | 'sunlight_lamp' | 'twilight_lamp' | 'day_lamp_2700_6500k' | 'night_lamp_2700_6500k',
    dimensions?: { width: number; height: number },
    userEmail?: string,
    jobMeta?: { clientItemId: string; historyPayloadDraft: Record<string, unknown> },
  ) {
    const normalized = await normalizeImageForRunningHubBody(image);
    return request('/api/gemini/runninghub/relight', {
      method: 'POST',
      timeout: 300000,
      body: JSON.stringify({ image: normalized, mode, dimensions, userEmail, ...jobMeta }),
    });
  },

  /** 文本重构：POST /api/gemini/runninghub/text-reconstruct（RUNNINGHUB_TEXT_RECONSTRUCT_WEBAPP_ID） */
  async submitTextReconstructRunningHub(
    image: string,
    text: string,
    dimensions?: { width: number; height: number },
    userEmail?: string,
    jobMeta?: { clientItemId: string; historyPayloadDraft: Record<string, unknown> },
  ) {
    const normalized = await normalizeImageForRunningHubBody(image);
    return request('/api/gemini/runninghub/text-reconstruct', {
      method: 'POST',
      timeout: 300000,
      body: JSON.stringify({ image: normalized, text: text.trim(), dimensions, userEmail, ...jobMeta }),
    });
  },

  /** 物理重构：POST /api/gemini/runninghub/physical-reconstruct（一次返回 4 张） */
  async submitPhysicalReconstructRunningHub(
    image: string,
    dimensions?: { width: number; height: number },
    userEmail?: string,
    jobMeta?: { clientItemId: string; historyPayloadDraft: Record<string, unknown> },
  ) {
    const normalized = await normalizeImageForRunningHubBody(image);
    return request('/api/gemini/runninghub/physical-reconstruct', {
      method: 'POST',
      timeout: 300000,
      body: JSON.stringify({ image: normalized, dimensions, userEmail, ...jobMeta }),
    });
  },

  /** RunningHub 侧栏历史（Supabase 仅存 payload JSON，图片在 COS / RunningHub URL） */
  async listRunningHubFeatureHistory(
    featureType?: 'upscale' | 'tone_mapping' | 'light_reconstruct' | 'text_reconstruct' | 'physical_reconstruct',
  ) {
    const q = featureType ? `?featureType=${encodeURIComponent(featureType)}` : '';
    return request(`/api/gemini/runninghub-feature-history${q}`, { method: 'GET' });
  },

  async upsertRunningHubFeatureHistory(
    body: {
      featureType: 'upscale' | 'tone_mapping' | 'light_reconstruct' | 'text_reconstruct' | 'physical_reconstruct';
      clientItemId: string;
      payload: unknown;
    },
    opts?: { timeout?: number },
  ) {
    return request('/api/gemini/runninghub-feature-history', {
      method: 'POST',
      body: JSON.stringify(body),
      timeout: opts?.timeout ?? 180000,
    });
  },

  async deleteRunningHubFeatureHistoryByClient(
    featureType: 'upscale' | 'tone_mapping' | 'light_reconstruct' | 'text_reconstruct' | 'physical_reconstruct',
    clientItemId: string,
  ) {
    const q = `?featureType=${encodeURIComponent(featureType)}&clientItemId=${encodeURIComponent(clientItemId)}`;
    return request(`/api/gemini/runninghub-feature-history${q}`, { method: 'DELETE' });
  },

  // 获取历史记录（后端晚于 Vite 启动时多试几次，避免一进来存档为空）
  async getHistory() {
    const maxAttempts = 5;
    const pauseMs = 1200;
    let lastErr: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        return await request('/api/gemini/history', {
          method: 'GET',
        });
      } catch (e: unknown) {
        lastErr = e;
        const msg = String((e as Error)?.message ?? e);
        const retryable =
          msg.includes('无法连接后端') ||
          msg.includes('Unable to connect') ||
          msg.includes('Failed to fetch') ||
          msg.includes('Load failed');
        if (attempt < maxAttempts - 1 && retryable) {
          await new Promise((r) => setTimeout(r, pauseMs));
          continue;
        }
        throw e;
      }
    }
    throw lastErr;
  },

  // 通过后端代理拉取图片并返回 dataURL(base64)（解决浏览器跨域/CORS）
  async fetchImageDataUrl(url: string) {
    return request('/api/gemini/fetch-image', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  },

  /** 将 data URL 上传至腾讯云 COS（photopea/ 前缀），返回公网 HTTPS URL，供 Photopea # API 使用 */
  async uploadImageForPhotopea(image: string) {
    return request('/api/gemini/upload-for-photopea', {
      method: 'POST',
      body: JSON.stringify({ image }),
      timeout: 180000,
    });
  },
  
  // 删除历史记录
  async deleteHistory(id: string) {
    return request(`/api/gemini/history/${id}`, {
      method: 'DELETE',
    });
  },
  
  // Upscale API
  async upscaleImage(
    image: string,
    scaleValue: number,
    userEmail?: string,
    jobMeta?: { clientItemId: string; historyPayloadDraft: Record<string, unknown> },
  ) {
    return request('/api/upscale/upscale', {
      method: 'POST',
      body: JSON.stringify({ image, scaleValue, userEmail, ...jobMeta }),
      timeout: 300000,
    });
  },
  
  async getRemainingUpscales(userEmail?: string) {
    if (!userEmail) {
      return Promise.resolve({ remainingUpscales: null });
    }
    return request('/api/upscale/remaining', {
      method: 'POST',
      body: JSON.stringify({ userEmail }),
    });
  },
  
  // 灯光控制 API
  async updateLight(brightness: number, colorTemp: string) {
    return request('/api/update-light', {
      method: 'POST',
      body: JSON.stringify({ brightness, colorTemp }),
    });
  },

  /**
   * 验证码邮件由后端 nemo-light-backend 处理：
   * POST /api/auth/send-verification-code → routes/auth.ts → emailService（优先 Resend HTTPS API，其次 SMTP）。
   * 部署时在 nemo-light-backend/.env 配置 RESEND_API_KEY=re_... 或 SMTP_*（QQ 建议 465 + SMTP_SECURE=true）。
   */
  async sendVerificationCode(email: string, password: string, opts?: { register?: boolean }) {
    return request('/api/auth/send-verification-code', {
      method: 'POST',
      body: JSON.stringify({ email, password, register: opts?.register === true }),
      skipAuth: true,
    });
  },

  async verifyCode(email: string, code: string, password: string) {
    return request('/api/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify({ email, code, password }),
      skipAuth: true,
    });
  },

  /** POST /api/auth/ensure-public-user — 登录后幂等创建 public.users */
  async ensurePublicUser() {
    return request('/api/auth/ensure-public-user', { method: 'POST', timeout: 30000 });
  },

  /** GET /api/credits/account：服务端校验 JWT 后查 public.users + credits_ledger */
  async getCreditsAccount() {
    return request('/api/credits/account', { method: 'GET', timeout: 60000 });
  },

  /** 通用 POST（相对路径），自动带 JSON 与 Bearer */
  async post(path: string, body: Record<string, unknown>, init?: { timeout?: number }) {
    return request(path, {
      method: 'POST',
      body: JSON.stringify(body),
      timeout: init?.timeout ?? 60000,
    });
  },

  /** GET /api/credits/plan-deduct：档位单价（仅服务端有权访问数据库） */
  async getCreditsPlanDeduct(featureType: string) {
    const q = encodeURIComponent(featureType);
    return request(`/api/credits/plan-deduct?feature_type=${q}`, { method: 'GET', timeout: 60000 });
  },

  async listActiveRunningHubJobs() {
    return request('/api/runninghub-jobs/active', { method: 'GET', timeout: 60000 });
  },

  async getRunningHubJob(jobId: string) {
    return request(`/api/runninghub-jobs/${encodeURIComponent(jobId)}`, {
      method: 'GET',
      timeout: 150000,
    });
  },

  async ackRunningHubJob(jobId: string) {
    return request(`/api/runninghub-jobs/${encodeURIComponent(jobId)}/ack`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
};


